import React, { useState, useRef, useEffect } from 'react';

interface CustomImageViewerProps {
  src: string;
  alt: string;
  className?: string;
}

const CustomImageViewer: React.FC<CustomImageViewerProps> = ({ 
  src, 
  alt,
  className = "" 
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    setIsZoomed(false); // Reset zoom when toggling fullscreen
  };
  
  const toggleZoom = () => {
    setIsZoomed(!isZoomed);
  };
  
  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        setIsZoomed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);
  
  // Prevent scrolling when in fullscreen mode
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);
  
  // Helper to check if we're inside a heading element
  const isInsideHeading = () => {
    if (typeof window === 'undefined' || !containerRef.current) return false;
    
    let parent = containerRef.current.parentElement;
    while (parent) {
      const tagName = parent.tagName.toLowerCase();
      if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  };
  
  // Helper to check if we're inside a quiz question
  const isInQuizContext = () => {
    if (typeof window === 'undefined' || !containerRef.current) return false;
    
    let parent = containerRef.current.parentElement;
    while (parent) {
      if (parent.classList.contains('quiz-image-placeholder') || 
          parent.classList.contains('question-content') ||
          parent.classList.contains('card-title')) {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  };
  
  if (hasError) {
    return (
      <div className="image-error-container border rounded p-4 bg-gray-100 dark:bg-gray-800 text-center">
        <p className="text-muted-foreground">Unable to load image</p>
        <p className="text-sm text-muted-foreground mt-1">{alt}</p>
      </div>
    );
  }
  
  return (
    <>
      <div 
        ref={containerRef}
        className={`image-wrapper-container relative rounded-md overflow-hidden ${className} ${isInsideHeading() ? 'inside-heading' : ''} ${isInQuizContext() ? 'in-quiz' : ''}`}
      >
        <img 
          ref={imageRef}
          src={src} 
          alt={alt} 
          className={`max-w-full h-auto rounded-md cursor-pointer transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onClick={toggleFullscreen}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
        
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          </div>
        )}
        
        {isLoaded && alt && (
          <div className={`w-full text-sm text-center mt-1 text-muted-foreground image-caption ${isInsideHeading() || isInQuizContext() ? 'compact-caption' : ''}`}>
            {alt}
          </div>
        )}
      </div>
      
      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={toggleFullscreen}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img 
              src={src} 
              alt={alt} 
              className={`max-w-full max-h-[90vh] object-contain transition-all duration-300 ${isZoomed ? 'scale-150 cursor-zoom-out' : 'cursor-zoom-in'} margin-top-auto`}
              onClick={toggleZoom}
              style={{marginTop: 'auto'}}
            />
            
            {alt && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2 text-white text-center fullscreen-caption">
                {alt}
              </div>
            )}
            
            <button 
              className="absolute top-4 right-4 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
              onClick={toggleFullscreen}
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomImageViewer; 